/**
 * Constants for PBKDF2
 */
const PBKDF2_KEY_LENGTH = 32 // 256 bits
const PBKDF2_ITERATIONS = 100000 // Default number of iterations

/**
 * Derive a key using PBKDF2-HMAC-SHA256
 *
 * @param baseKey - Base key (string)
 * @param seed - Salt for derivation (string)
 * @returns Promise<Uint8Array> - Derived key of 32 bytes
 */
export const deriveKeyFromBaseAndSeed = async (
  baseKey: string,
  seed: string,
): Promise<Uint8Array> => {
  const encoder = new TextEncoder()

  // Import the base key as a raw key to use with PBKDF2
  const baseKeyData = await crypto.subtle.importKey(
    'raw',
    encoder.encode(baseKey),
    'PBKDF2',
    false,
    ['deriveBits'],
  )

  // Derive the bits using PBKDF2 with SHA-256
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: encoder.encode(seed),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKeyData,
    PBKDF2_KEY_LENGTH * 8, // Size in bits (32 bytes = 256 bits)
  )

  return new Uint8Array(derivedBits)
}

/**
 * Constants for AES-GCM
 */
const GCM_IV_LENGTH = 12 // 12 bytes for AES-GCM IV

/**
 * Cryptographs data using AES-GCM with random IV
 * Returns: IV (12 bytes) || Ciphertext || Tag (16 bytes)
 *
 * @param key - Key of 32 bytes (Uint8Array)
 * @param plaintext - Plaintext to encrypt
 * @returns Promise<Uint8Array> - IV + Ciphertext + Tag
 */
export const encryptGcm = async (key: Uint8Array, plaintext: string): Promise<Uint8Array> => {
  try {
    // Validate if the key has 32 bytes
    if (key.length !== 32) {
      throw new Error('A chave deve ter exatamente 32 bytes (256 bits)')
    }

    // Generate random IV of 12 bytes
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_LENGTH))

    // Import the key for use with AES-GCM
    // Create a copy of the Uint8Array to ensure it is a valid BufferSource
    const keyCopy = new Uint8Array(key)
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyCopy,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt'],
    )

    // Encode the plaintext
    const encoder = new TextEncoder()
    const plaintextBuffer = encoder.encode(plaintext)

    // Encrypt (includes tag automatically in AES-GCM)
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      cryptoKey,
      plaintextBuffer,
    )

    // Convert ciphertext to Uint8Array
    const ciphertextArray = new Uint8Array(ciphertext)

    // Return IV + Ciphertext (which already includes the tag of 16 bytes)
    const result = new Uint8Array(iv.length + ciphertextArray.length)
    result.set(iv, 0)
    result.set(ciphertextArray, iv.length)

    return result
  } catch (error) {
    throw new Error(
      `Erro ao criptografar dados: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

/**
 * Convert a Uint8Array to Base64
 *
 * @param uint8Array - Array of bytes to be converted
 * @returns string - String encoded in Base64
 */
export const uint8ArrayToBase64 = (uint8Array: Uint8Array): string => {
  let binary = ''
  const len = uint8Array.byteLength
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(uint8Array[i])
  }
  return btoa(binary)
}

/**
 * Add prefix and suffix to Base64 data
 *
 * @param data - Data in Base64
 * @param prefix - Prefix to add
 * @param suffix - Suffix to add
 * @returns string - Obfuscated data with prefix + data + suffix
 */
export const addObfuscation = (data: string, prefix: string, suffix: string): string =>
  `${prefix}${data}${suffix}`

/**
 * Encrypt any string
 * @param seed - Seed retrieved from /seed endpoint
 * @param stringToEncrypt - The string that will be encrypted
 * @returns string - Encrypted string
 */
export const encryptString = async (seed: string, stringToEncrypt: string) => {
  const secretBaseKey = process.env.REACT_APP_SECRET_BASE_KEY

  const derivedKey = await deriveKeyFromBaseAndSeed(secretBaseKey ?? '', seed)

  const encryptedPayload = await encryptGcm(derivedKey, stringToEncrypt)

  const encoded = uint8ArrayToBase64(encryptedPayload)

  const obfuscatedPayload = addObfuscation(encoded, 'bx_', '_xb')

  return obfuscatedPayload
}
